import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Typography, Card, notification, Button } from 'antd';
import fitMatchIcon from "../../assets/icons/logo.png";
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            duration: 0.4,
            staggerChildren: 0.05,
        },
    },
};

const itemVariants = {
    hidden: { opacity: 0, y: 5 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
};

const logoVariants = {
    hidden: { scale: 0.98, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { duration: 0.3, type: "spring", stiffness: 400 } },
};

const AuthLayout = ({ 
    headerRenderer,
    formContent, 
    footer = true,
    showTermsPrivacyLinks = true,
    title = "Welcome Back",
    subtitle = "Sign in to your admin dashboard",
    showLogo = true,
    showSubtitle = true,
    onError = (msg) => notification.open({ message: msg, icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} />, duration: 3 }),
    onSuccess = (msg) => notification.open({ message: msg, icon: <CheckCircleOutlined style={{ color: 'var(--primary-purple)' }} />, duration: 3 })
}) => {
    const routerHistory = useHistory();
    const [dimensions, setDimensions] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 1024,
        height: typeof window !== 'undefined' ? window.innerHeight : 768
    });

    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = dimensions.width < 768;
    const isSmallHeight = dimensions.height < 650;

    const spacing = isSmallHeight ? '1px' : isMobile ? '2px' : '4px';
    const fieldGap = isSmallHeight ? '1px' : isMobile ? '2px' : '4px';
    const logoSize = isSmallHeight ? '36px' : isMobile ? '44px' : '74px';
    const titleSize = isSmallHeight ? '0.875rem' : isMobile ? '1.125rem' : '1.375rem';
    const textSize = isSmallHeight ? '0.5rem' : isMobile ? '0.625rem' : '0.75rem';
    const buttonHeight = isSmallHeight ? '28px' : isMobile ? '32px' : '40px';
    const buttonFont = isSmallHeight ? '0.5rem' : isMobile ? '0.625rem' : '0.75rem';
    const inputHeight = isSmallHeight ? '32px' : isMobile ? '36px' : '42px';
    const formSize = isSmallHeight ? 'small' : isMobile ? 'middle' : 'large';
    const cardPadding = isSmallHeight ? '4px' : isMobile ? '8px' : '16px';
    const outerPadding = isSmallHeight ? '1px' : isMobile ? '2px' : '8px';
    const logoPadding = isSmallHeight ? '2px' : isMobile ? '4px' : '12px';
    const sectionGap = isSmallHeight ? '2px' : isMobile ? '4px' : '8px';
    const dividerMargin = isSmallHeight ? '1px 0' : isMobile ? '2px 0' : '4px 0';
    const footerFont = isSmallHeight ? '0.5rem' : isMobile ? '0.5rem' : '0.625rem';
    const formWidth = isSmallHeight ? '260px' : isMobile ? '300px' : '340px';
    const iconSize = isSmallHeight ? '56px' : isMobile ? '72px' : '90px';
    const otpInputSize = isSmallHeight ? '36px' : isMobile ? '44px' : '52px';
    const otpInputFont = isSmallHeight ? '1rem' : isMobile ? '1.125rem' : '1.25rem';

    const responsiveProps = {
        isMobile,
        isSmallHeight,
        spacing,
        fieldGap,
        logoSize,
        titleSize,
        textSize,
        buttonHeight,
        buttonFont,
        inputHeight,
        formSize,
        cardPadding,
        outerPadding,
        logoPadding,
        sectionGap,
        dividerMargin,
        footerFont,
        formWidth,
        iconSize,
        otpInputSize,
        otpInputFont
    };

    const outerStyle = {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        height: '100vh',
        width: '100%',
        padding: isSmallHeight ? '12px' : isMobile ? '16px' : '24px',
        background: 'var(--dark-bg-start)',
        overflow: 'auto',
        boxSizing: 'border-box',
    };

    const bubbles = [
        { size: 120, left: '8%', top: '12%' },
        { size: 80, left: '85%', top: '18%' },
        { size: 160, left: '72%', top: '55%' },
        { size: 60, left: '15%', top: '70%' },
        { size: 100, left: '50%', top: '25%' },
        { size: 90, left: '25%', top: '45%' },
        { size: 70, left: '88%', top: '78%' },
        { size: 130, left: '5%', top: '35%' },
        { size: 55, left: '65%', top: '15%' },
        { size: 85, left: '40%', top: '75%' },
        { size: 95, left: '92%', top: '42%' },
        { size: 75, left: '55%', top: '60%' },
    ];

    const formItemStyle = {
        marginBottom: fieldGap,
        '& .ant-form-item-label': { marginBottom: '0 !important', lineHeight: '1.2' },
        '& .ant-form-item-control': { marginBottom: '0 !important' },
    };

    const buttonItemStyle = {
        marginBottom: 0,
        marginTop: fieldGap,
    };

    const dividerItemStyle = {
        margin: dividerMargin,
    };

    const forgotDivStyle = {
        textAlign: 'center',
        flexShrink: 0,
        marginTop: 0,
        paddingTop: 0,
    };

    const commonProps = {
        formItemStyle,
        buttonItemStyle,
        dividerItemStyle,
        forgotDivStyle,
        ...responsiveProps,
        onError,
        onSuccess
    };

    return (
        <div className="login-container" style={outerStyle} role="main">
            <div className="auth-bubbles" aria-hidden="true">
                {bubbles.map((b, i) => (
                    <div
                        key={i}
                        className="auth-bubble"
                        style={{
                            width: b.size,
                            height: b.size,
                            left: b.left,
                            top: b.top,
                            animationDelay: `${(i % 5) * 1.2}s`,
                        }}
                    />
                ))}
            </div>
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                style={{
                    position: 'relative',
                    zIndex: 1, 
                    width: '100%', 
                    maxWidth: '420px', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    boxSizing: 'border-box',
                }}
            >
                {/* Auth card: logo + welcome + form as one composition */}
                <motion.div
                    variants={itemVariants}
                    style={{
                        width: '100%',
                        flex: 'none',
                        marginBottom: sectionGap,
                    }}
                >
                    <Card
                        className="auth-login-card"
                        style={{
                            background: 'rgba(255, 255, 255, 0.88)',
                            border: '1px solid rgba(254, 2, 125, 0.14)',
                            borderRadius: '24px',
                            boxShadow:
                                '0 4px 24px rgba(254, 2, 125, 0.08), 0 12px 40px rgba(0, 0, 0, 0.06)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            width: '100%',
                            overflow: 'hidden',
                        }}
                        bodyStyle={{
                            padding: 0,
                            display: 'block',
                        }}
                        bordered={false}
                    >
                        <div
                            style={{
                                height: 4,
                                background: 'var(--gradient-primary)',
                                width: '100%',
                            }}
                        />
                        <div
                            style={{
                                padding: isSmallHeight
                                    ? '16px 16px 12px'
                                    : isMobile
                                      ? '20px 20px 16px'
                                      : '28px 28px 20px',
                            }}
                        >
                            <motion.div
                                variants={logoVariants}
                                style={{
                                    textAlign: 'center',
                                    marginBottom: isSmallHeight ? 12 : 20,
                                }}
                            >
                                {headerRenderer ? (
                                    headerRenderer(responsiveProps)
                                ) : (
                                    <>
                                        {showLogo && (
                                            <motion.img
                                                src={fitMatchIcon}
                                                alt="Tandemly"
                                                style={{
                                                    width: isMobile ? '140px' : '168px',
                                                    height: 'auto',
                                                    maxHeight: isSmallHeight ? '64px' : '88px',
                                                    objectFit: 'contain',
                                                    display: 'block',
                                                    margin: '0 auto',
                                                }}
                                                whileHover={{ scale: 1.03 }}
                                            />
                                        )}
                                        <Title
                                            level={3}
                                            style={{
                                                margin: showLogo
                                                    ? `${isSmallHeight ? 10 : 16}px 0 0`
                                                    : 0,
                                                color: 'var(--text-white)',
                                                fontSize: isSmallHeight
                                                    ? '1.125rem'
                                                    : isMobile
                                                      ? '1.35rem'
                                                      : '1.5rem',
                                                fontWeight: 700,
                                                letterSpacing: '-0.02em',
                                                lineHeight: 1.25,
                                            }}
                                        >
                                            {title}
                                        </Title>
                                        {showSubtitle && (
                                            <Text
                                                style={{
                                                    color: 'var(--text-gray)',
                                                    fontSize: isSmallHeight
                                                        ? '0.75rem'
                                                        : '0.875rem',
                                                    display: isSmallHeight ? 'none' : 'block',
                                                    marginTop: 6,
                                                    lineHeight: 1.4,
                                                }}
                                            >
                                                {subtitle}
                                            </Text>
                                        )}
                                    </>
                                )}
                            </motion.div>

                            <div
                                style={{
                                    height: 1,
                                    background:
                                        'linear-gradient(90deg, transparent, rgba(254, 2, 125, 0.18), transparent)',
                                    marginBottom: isSmallHeight ? 12 : 18,
                                }}
                            />

                            {formContent(commonProps)}
                        </div>
                    </Card>
                </motion.div>

                {/* Footer */}
                {footer && (
                    <motion.div
                        variants={itemVariants}
                        style={{
                            textAlign: 'center',
                            flexShrink: 0,
                            width: '100%',
                            paddingTop: 0,
                            marginBottom: 0,
                        }}
                    >
                        <Text style={{ color: 'var(--text-gray-dark)', fontSize: footerFont }}>
                            © {new Date().getFullYear()} Tandemly. All rights reserved.
                        </Text>
                    </motion.div>
                )}
            </motion.div>

            {/* Terms | Privacy - fixed at bottom right (hidden on Terms/Privacy pages) */}
            {footer && showTermsPrivacyLinks && (
                <div
                    style={{
                        color: '#FE027D',
                        position: 'fixed',
                        bottom: 36,
                        right: 41,
                        zIndex: 10,
                        fontSize: footerFont,
                    }}
                >
                    <Button
                        type="link"
                        onClick={() => routerHistory.push('/terms-and-policy')}
                        style={{
                            color: '#FE027D',
                            padding: '0 4px',
                            fontSize: '14px',
                            height: 'auto',
                            lineHeight: 1,
                        }}
                    >
                        Terms & Policy
                    </Button>
                    <Text style={{ color: 'var(--text-gray-dark)', margin: '0 2px' }}>|</Text>
                    <Button
                        type="link"
                        onClick={() => routerHistory.push('/privacy-policy')}
                        style={{
                            color: '#FE027D',
                            padding: '0 4px',
                            fontSize: '14px',
                            height: 'auto',
                            lineHeight: 1,
                        }}
                    >
                        Privacy Policy
                    </Button>
                </div>
            )}
        </div>
    );
};

export default AuthLayout;